/**
 * Enhanced Live Event Feed
 * Premium feed with slide-in animations and gradient icons
 */

import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { liveEventsService, type LiveEvent } from '../services/liveEventsService'
import { useAuthStore } from '../stores/authStore'
import { formatTime } from '../utils/dateUtils'
import { 
  EventTypeIcon, 
  PlatformIcon, 
  LocationIcon, 
  LoadingIcon,
  OfflineIcon,
  LockIcon,
  OnlineIcon
} from './icons'
import Card from './ui/Card'
import Badge from './ui/Badge'
import { formatPlatform, formatSdkPlatform } from '../utils/platform'

const MAX_EVENTS = 50 // Keep last 50 events
/**
 * The feed used to setState per arriving event, re-running 50 framer-motion
 * spring animations each time. Batching arrivals into one state update per
 * interval keeps the feed readable and caps its render cost regardless of how
 * fast events stream in.
 */
const FEED_FLUSH_INTERVAL_MS = 500

interface LiveEventFeedProps {
  appId?: string // Filter events for specific app
}

function normalizeLiveEvent(event: LiveEvent): LiveEvent {
  return {
    ...event,
    id: event.id || `${event.appId || 'app'}-${event.type || 'event'}-${event.timestamp || Date.now()}`,
    timestamp: event.timestamp || new Date().toISOString(),
    appName: event.appName || event.appId || 'Unknown app',
    type: event.type || 'custom_event',
  }
}

export default function LiveEventFeed({ appId }: LiveEventFeedProps = {}) {
  const { user, checkAuth } = useAuthStore()
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const eventsEndRef = useRef<HTMLDivElement>(null)
  const pendingEvents = useRef<LiveEvent[]>([])

  useEffect(() => {
    if (!user) return

    // Connect to live events
    setIsConnecting(true)
    setTokenExpired(false)
    liveEventsService
      .connect(user.id, user.id, appId) // Pass appId for filtering
      .then(() => {
        setIsConnecting(false)
        // If the singleton was already connected (e.g. another component connected it
        // first), the 'connected' event already fired before our callback was registered.
        // Sync state immediately so we don't stay stuck on "Offline".
        if (liveEventsService.getIsConnected()) {
          setIsConnected(true)
        }
      })
      .catch((error) => {
        console.error('Failed to connect to live events:', error)
        setIsConnecting(false)
      })

    // Subscribe to events. Arrivals are buffered and flushed on a timer so the
    // render cost of the feed stays flat as the event rate climbs.
    const unsubscribeEvents = liveEventsService.onEvent((event) => {
      const normalizedEvent = normalizeLiveEvent(event)
      // Filter by app if appId is provided
      if (appId && normalizedEvent.appId !== appId) return

      const buffer = pendingEvents.current
      buffer.unshift(normalizedEvent)
      if (buffer.length > MAX_EVENTS) buffer.length = MAX_EVENTS
    })

    const flushInterval = window.setInterval(() => {
      if (pendingEvents.current.length === 0) return
      const arrived = pendingEvents.current
      pendingEvents.current = []
      setEvents((prev) => [...arrived, ...prev].slice(0, MAX_EVENTS))
    }, FEED_FLUSH_INTERVAL_MS)

    // Subscribe to connection state
    const unsubscribeConnected = liveEventsService.onConnected(() => {
      setIsConnected(true)
      setTokenExpired(false)
    })

    const unsubscribeDisconnected = liveEventsService.onDisconnected(() => {
      setIsConnected(false)
    })

    // Subscribe to token expiration
    const unsubscribeTokenExpired = liveEventsService.onTokenExpired(() => {
      console.warn('Live Events token expired - user needs to log in again')
      setTokenExpired(true)
      setIsConnected(false)
      // Don't try to reconnect - user needs to log in again to get a fresh token
    })

    // Cleanup — only unsubscribe callbacks; do NOT call disconnect() here because
    // liveEventsService is a singleton shared with parent components (e.g. AppDetail).
    // Tearing down the connection from a child widget would kill live data for the
    // entire page.  Connection lifecycle is managed by the top-level consumer.
    return () => {
      unsubscribeEvents()
      unsubscribeConnected()
      unsubscribeDisconnected()
      unsubscribeTokenExpired()
      window.clearInterval(flushInterval)
      pendingEvents.current = []
    }
  }, [user, checkAuth, appId])

  const getEventVariant = (type: LiveEvent['type']): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (type) {
      case 'session_start':
        return 'success'
      case 'session_end':
        return 'default'
      case 'screen_view':
        return 'info'
      case 'crash':
        return 'error'
      default:
        return 'info'
    }
  }

  if (isConnecting) {
    return (
      <Card className="h-full flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <LoadingIcon size="2xl" className="mx-auto mb-3 animate-spin text-primary-indigo" />
          <p className="text-gray-400">Connecting to live events...</p>
        </motion.div>
      </Card>
    )
  }

  return (
    <Card className="h-full flex flex-col" padding="none">
      {/* Header */}
      <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-lg">Live Events</h3>
          {isConnected ? (
            <Badge variant="success" dot size="sm">
              Connected
            </Badge>
          ) : tokenExpired ? (
            <Badge variant="warning" size="sm" title="Session expired - please refresh the page">
              <OfflineIcon size="xs" />
              Session Expired
            </Badge>
          ) : (
            <Badge variant="default" size="sm">
              <OfflineIcon size="xs" />
              Offline
            </Badge>
          )}
        </div>
        <Badge variant="gradient" size="sm">
          {events.length}
        </Badge>
      </div>

      {/* Events list */}
      <div className="flex-1 overflow-auto custom-scrollbar p-4 space-y-2">
        <AnimatePresence initial={false}>
          {events.length === 0 ? (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <motion.div
                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-white/5 text-indigo-200"
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {tokenExpired ? <LockIcon size="2xl" /> : isConnected ? <OnlineIcon size="2xl" /> : <OfflineIcon size="2xl" />}
              </motion.div>
              <p className="text-gray-400 text-sm">
                {tokenExpired 
                  ? 'Session expired - please refresh the page to reconnect' 
                  : isConnected 
                    ? 'Connected - waiting for new events from your app...' 
                    : 'Connecting to live event stream...'}
              </p>
              {isConnected && (
                <p className="text-gray-500 text-xs mt-2">
                  Send test events from your app to see them appear here in real-time
                </p>
              )}
            </motion.div>
          ) : (
            events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.95 }}
                transition={{ 
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  delay: index < 3 ? index * 0.05 : 0
                }}
                className="relative"
              >
                <div className="p-4 rounded-xl bg-white/5 hover:bg-white/8 transition-all duration-300 group border border-white/10 hover:border-white/20">
                  <div className="flex items-start gap-3">
                    {/* Event icon with gradient background */}
                    <div className={`
                      w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                      ${getEventVariant(event.type) === 'success' && 'bg-green-500/20'}
                      ${getEventVariant(event.type) === 'info' && 'bg-blue-500/20'}
                      ${getEventVariant(event.type) === 'error' && 'bg-red-500/20'}
                      ${getEventVariant(event.type) === 'default' && 'bg-gray-500/20'}
                    `}>
                      <EventTypeIcon type={event.type} size="md" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Event header - Show meaningful title */}
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={getEventVariant(event.type)} size="sm">
                          {event.type.replace('_', ' ')}
                        </Badge>
                        {event.platform && (
                          <Badge variant="default" size="sm" className="gap-1 font-normal border-white/15">
                            <PlatformIcon platform={event.platform} size="xs" className="text-gray-400" />
                            <span className="text-[10px] text-gray-400">{formatPlatform(event.platform)}</span>
                          </Badge>
                        )}
                        {(event.sdkVersion || event.sdkPlatform) && (
                          <Badge variant="default" size="sm" className="font-normal border-white/15">
                            <span className="text-[10px] text-gray-400">
                              {[event.sdkPlatform ? formatSdkPlatform(event.sdkPlatform) : null, event.sdkVersion].filter(Boolean).join(' ')}
                            </span>
                          </Badge>
                        )}
                      </div>
                      
                      {/* Event details - Show clear, descriptive title */}
                      <p className="text-sm font-semibold text-gray-100 mb-1">
                        {event.type === 'session_start' && 'User Started Session'}
                        {event.type === 'session_end' && 'User Ended Session'}
                        {event.type === 'screen_view' && (event.screenName || 'Screen Viewed')}
                        {event.type === 'crash' && 'App Crashed'}
                        {event.type === 'custom_event' && (event.eventName || 'Custom Event')}
                      </p>
                      
                      {/* App name and additional context */}
                      <p className="text-xs text-gray-400 mb-2 truncate">
                        <span className="font-medium">{event.appName}</span>
                        {event.appVersion && <span> • v{event.appVersion}</span>}
                        {event.buildNumber && <span> ({event.buildNumber})</span>}
                        {event.type === 'custom_event' && !event.eventName && <span className="text-yellow-500 ml-2">No event name</span>}
                        {event.type === 'screen_view' && event.screenName && <span> • {event.screenName}</span>}
                      </p>
                      
                      {/* Event metadata */}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {event.country && (
                          <span className="flex items-center gap-1">
                            <LocationIcon size="xs" />
                            {event.country}
                            {event.city && `, ${event.city}`}
                          </span>
                        )}
                        <span>{formatTime(event.timestamp)}</span>
                      </div>
                      
                      {/* Crash reason */}
                      {event.crashReason && (
                        <p className="text-xs text-red-400 mt-2 truncate bg-red-500/10 px-2 py-1 rounded">
                          {event.crashReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
        <div ref={eventsEndRef} />
      </div>
    </Card>
  )
}
